import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';

export default function PrivacyPolicyPage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-policy-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back link */}
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 mb-8 hover:text-[#00C8EB] transition-colors ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
              <ShieldCheck size={28} weight="duotone" className="text-[#00C8EB]" />
            </div>
            <div>
              <h1 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Privacy Policy
              </h1>
              <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: March 2026
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={`prose prose-lg max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
            <div className={`space-y-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              
              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  1. Introduction
                </h2>
                <p>
                  Atlas Softech Limited ("we," "our," or "us") is committed to protecting your personal data and respecting your privacy in accordance with the <strong>Personal Data Protection Act of the United Republic of Tanzania</strong>, the <strong>Electronic and Postal Communications Act (EPOCA)</strong>, and other applicable data protection laws and regulations.
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, store, disclose, and protect your personal information when you use our website, services, and products. By accessing our services, you consent to the collection and use of your information as described in this policy.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  2. Data Controller Information
                </h2>
                <p>
                  Atlas Softech is in charge of managing and safeguarding your personal information. To guarantee the security and proper handling of your data, we have designated a Data Protection Officer who is tasked with addressing data protection inquiries related to this privacy policy. Should you have any questions regarding this privacy policy or wish to exercise your legal rights, please reach out to the data privacy officer using the contact information provided below.
                </p>
                <div className={`p-4 rounded-lg mt-4 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Atlas Softech Limited</strong></p>
                  <p>Data Protection Officer</p>
                  <p>Dar Es Salaam, Tanzania</p>
                  <p>Email: dpo@atlassoftech.com</p>
                  <p>Phone: +255 799 227 202</p>
                </div>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3. Personal Data We Collect
                </h2>
                <p>
                  In compliance with Tanzanian data protection laws, we collect and process the following categories of personal data:
                </p>
                <h3 className={`text-xl font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.1 Information You Provide Directly (With your consent)
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Full name and contact information (email address, phone number, physical address)</li>
                  <li>Company or organization name and position</li>
                  <li>Correspondence and communications with us</li>
                  <li>Service inquiries and project requirements</li>
                </ul>
                
                <h3 className={`text-xl font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.2 Information Collected Automatically
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address and device information</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
                
                <p className="mt-6">
                  We do not gather any Special Categories of Personal Data concerning you, which encompasses information about your race or ethnicity, sexual life, sexual orientation, religious or philosophical beliefs, political views, investment or trade/trust union membership, health details, and genetic or biometric data. Additionally, we do not collect any data regarding criminal convictions and offenses.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  4. Legal Basis for Processing
                </h2>
                <p>
                  Under the Personal Data Protection Act of Tanzania, we process your personal data based on the following legal grounds:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities</li>
                  <li><strong>Contractual Necessity:</strong> Where processing is necessary to perform a contract with you or take pre-contractual steps at your request</li>
                  <li><strong>Legal Obligation:</strong> Where processing is necessary to comply with Tanzanian law</li>
                  <li><strong>Legitimate Interests:</strong> Where processing is necessary for our legitimate business interests, provided these do not override your fundamental rights and freedoms</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  5. Purpose of Data Processing
                </h2>
                <p>
                  We use your personal data for the following purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To provide, maintain, and improve our services</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To send you service-related communications</li>
                  <li>To send marketing communications (with your consent)</li>
                  <li>To comply with legal and regulatory requirements under Tanzanian law</li>
                  <li>To protect our legal rights and prevent fraud</li>
                  <li>To analyze and improve our website and services</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  6. Data Sharing and Disclosure
                </h2>
                <p>
                  We may share your personal data with:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Providers:</strong> Third-party vendors who assist us in providing our services, bound by confidentiality agreements</li>
                  <li><strong>Legal Authorities:</strong> When required by Tanzanian law, court order, or government request</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
                </ul>
                <p className="mt-4">
                  We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  7. International Data Transfers
                </h2>
                <p>
                  Where we transfer personal data outside the United Republic of Tanzania, we ensure that appropriate safeguards are in place in accordance with Tanzanian data protection laws. Such transfers are only made to countries or organizations that provide adequate levels of data protection or where we have implemented appropriate contractual safeguards.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  8. Data Retention
                </h2>
                <p>
                  We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements under Tanzanian law. The retention period may vary depending on the context of the processing and our legal obligations.
                </p>
                <p>
                  When personal data is no longer required, we will securely delete or anonymize it in accordance with our data retention policies.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  9. Your Rights Under Tanzanian Law
                </h2>
                <p>
                  Under the Personal Data Protection Act and related Tanzanian legislation, you have the following rights:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Right of Access:</strong> The right to request access to your personal data and obtain a copy</li>
                  <li><strong>Right to Rectification:</strong> The right to request correction of inaccurate or incomplete data</li>
                  <li><strong>Right to Erasure:</strong> The right to request deletion of your personal data in certain circumstances</li>
                  <li><strong>Right to Restrict Processing:</strong> The right to request limitation of processing in certain circumstances</li>
                  <li><strong>Right to Data Portability:</strong> The right to receive your data in a structured, commonly used format</li>
                  <li><strong>Right to Object:</strong> The right to object to processing based on legitimate interests or for direct marketing</li>
                  <li><strong>Right to Withdraw Consent:</strong> The right to withdraw consent at any time where processing is based on consent</li>
                </ul>
                <p className="mt-4">
                  To exercise any of these rights, please contact us using the details provided in Section 2.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  10. Data Security
                </h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure access controls and authentication</li>
                  <li>Regular security assessments and updates</li>
                  <li>Staff training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  11. Children's Privacy
                </h2>
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child without parental consent, we will take steps to delete such information.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  12. Complaints and Regulatory Authority
                </h2>
                <p>
                  If you believe that your data protection rights have been violated, you have the right to lodge a complaint with the relevant regulatory authority in Tanzania. You may also contact us directly to resolve any concerns.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  13. Changes to This Privacy Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website with a new effective date. We encourage you to review this policy periodically.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  14. Contact Us
                </h2>
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className={`p-4 rounded-lg mt-4 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Atlas Softech Limited</strong></p>
                  <p>Data Protection Officer</p>
                  <p>Dar Es Salaam, Tanzania</p>
                  <p>Email: dpo@atlassoftech.com</p>
                  <p>Phone: +255 799 227 202</p>
                </div>
              </section>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
